import type { ChartDataPoint, VegaLiteSpec } from '../types/chart.ts';

/**
 * ChartSpecBuilder - Generates Vega-Lite specifications for common chart types
 *
 * This utility class provides reusable chart specifications that can be used
 * across the application for consistent data visualization.
 */
export class ChartSpecBuilder {
  private static readonly DEFAULT_COLOR_PALETTE = [
    '#0e7490', // teal
    '#6e44c1', // purple
    '#77bcc0', // light teal
    '#48276c', // dark purple
    '#998ce3', // light purple
    '#2563eb', // blue
    '#dc2626', // red
    '#059669', // green
    '#d97706', // orange
    '#7c2d12', // brown
  ];

  /**
   * Detect if dark mode is currently active
   */
  private static isDarkMode(): boolean {
    return document.documentElement.classList.contains('dark');
  }

  /**
   * Get theme-appropriate colors and configuration
   */
  private static getThemeConfig() {
    const isDark = ChartSpecBuilder.isDarkMode();
    return {
      background: 'transparent',
      textColor: isDark ? '#f9fafb' : '#1f2937',
      gridColor: isDark ? '#374151' : '#e5e7eb',
      axisColor: isDark ? '#6b7280' : '#9ca3af',
    };
  }

  /**
   * Generate a bar chart specification
   */
  static bar(
    data: ChartDataPoint[],
    options: {
      width?: number;
      height?: number;
      xTitle?: string;
      yTitle?: string;
      color?: string;
    } = {}
  ): VegaLiteSpec {
    const {
      width = 600,
      height = 400,
      xTitle = 'Category',
      yTitle = 'Count',
      color = ChartSpecBuilder.DEFAULT_COLOR_PALETTE[0],
    } = options;

    const theme = ChartSpecBuilder.getThemeConfig();

    return {
      $schema: 'https://vega.github.io/schema/vega-lite/v6.json',
      width,
      height,
      background: theme.background,
      padding: { left: 12, top: 12, right: 12, bottom: 12 },
      autosize: {
        type: 'fit',
        contains: 'padding',
      },
      data: { values: data },
      mark: { type: 'bar' as const },
      config: {
        axis: {
          labelColor: theme.textColor,
          titleColor: theme.textColor,
          domainColor: theme.axisColor,
          tickColor: theme.axisColor,
          gridColor: theme.gridColor,
        },
      },
      encoding: {
        x: {
          field: 'category',
          type: 'nominal' as const,
          title: xTitle,
          axis: {
            labelAngle: data.length > 5 ? -45 : 0, // Rotate labels for many categories
            labelLimit: 100,
          },
        },
        y: {
          field: 'value',
          type: 'quantitative' as const,
          title: yTitle,
        },
        color: { value: color },
        tooltip: [
          { field: 'value', type: 'quantitative' as const, title: 'Count' },
          { field: 'category', type: 'nominal' as const, title: 'Category' },
        ],
      },
    };
  }

  /**
   * Generate a line chart specification
   */
  static line(
    data: ChartDataPoint[],
    options: {
      width?: number;
      height?: number;
      xTitle?: string;
      yTitle?: string;
      color?: string;
      showPoints?: boolean;
    } = {}
  ): VegaLiteSpec {
    const {
      width = 600,
      height = 400,
      xTitle = 'Category',
      yTitle = 'Count',
      color = ChartSpecBuilder.DEFAULT_COLOR_PALETTE[1],
      showPoints = true,
    } = options;

    const theme = ChartSpecBuilder.getThemeConfig();

    return {
      $schema: 'https://vega.github.io/schema/vega-lite/v6.json',
      width,
      height,
      background: theme.background,
      padding: { left: 12, top: 12, right: 12, bottom: 12 },
      autosize: {
        type: 'fit',
        contains: 'padding',
      },
      data: { values: data },
      mark: {
        type: 'line' as const,
        point: showPoints,
        strokeWidth: 2,
      },
      config: {
        axis: {
          labelColor: theme.textColor,
          titleColor: theme.textColor,
          domainColor: theme.axisColor,
          tickColor: theme.axisColor,
          gridColor: theme.gridColor,
        },
      },
      encoding: {
        x: {
          field: 'category',
          type: 'ordinal' as const,
          title: xTitle,
        },
        y: {
          field: 'value',
          type: 'quantitative' as const,
          title: yTitle,
        },
        color: { value: color },
        tooltip: [
          { field: 'value', type: 'quantitative' as const, title: 'Count' },
          { field: 'category', type: 'ordinal' as const, title: 'Category' },
        ],
      },
    };
  }

  /**
   * Generate a pie chart specification
   */
  static pie(
    data: ChartDataPoint[],
    options: {
      width?: number;
      height?: number;
      colors?: string[];
      innerRadius?: number;
    } = {}
  ): VegaLiteSpec {
    const {
      width = 400,
      height = 400,
      colors = ChartSpecBuilder.DEFAULT_COLOR_PALETTE,
      innerRadius = 0,
    } = options;

    const theme = ChartSpecBuilder.getThemeConfig();

    return {
      $schema: 'https://vega.github.io/schema/vega-lite/v6.json',
      width,
      height,
      background: theme.background,
      padding: { left: 12, top: 12, right: 12, bottom: 12 },
      autosize: {
        type: 'fit',
        contains: 'padding',
      },
      data: { values: data },
      mark: {
        type: 'arc' as const,
        tooltip: true,
        innerRadius,
      },
      encoding: {
        theta: { field: 'value', type: 'quantitative' as const },
        color: {
          field: 'category',
          type: 'nominal' as const,
          scale: { range: colors },
          legend: {
            orient: 'bottom',
            titleLimit: 200,
            labelLimit: 300,
            labelColor: theme.textColor,
            titleColor: theme.textColor,
            columns: 2,
          },
        },
        tooltip: [
          { field: 'category', type: 'nominal' as const, title: 'Category' },
          { field: 'value', type: 'quantitative' as const, title: 'Count' },
        ],
      },
    };
  }

  /**
   * Generate a horizontal bar chart specification
   */
  static horizontalBar(
    data: ChartDataPoint[],
    options: {
      width?: number;
      height?: number;
      xTitle?: string;
      yTitle?: string;
      color?: string;
    } = {}
  ): VegaLiteSpec {
    const {
      width = 600,
      height = Math.max(300, data.length * 25), // Dynamic height based on data
      xTitle = 'Count',
      yTitle = 'Category',
      color = ChartSpecBuilder.DEFAULT_COLOR_PALETTE[0],
    } = options;

    const theme = ChartSpecBuilder.getThemeConfig();

    return {
      $schema: 'https://vega.github.io/schema/vega-lite/v6.json',
      width,
      height,
      background: theme.background,
      padding: { left: 12, top: 12, right: 12, bottom: 12 },
      autosize: {
        type: 'fit',
        contains: 'padding',
      },
      data: { values: data },
      mark: { type: 'bar' as const },
      config: {
        axis: {
          labelColor: theme.textColor,
          titleColor: theme.textColor,
          domainColor: theme.axisColor,
          tickColor: theme.axisColor,
          gridColor: theme.gridColor,
        },
      },
      encoding: {
        y: {
          field: 'category',
          type: 'ordinal' as const,
          title: yTitle,
          sort: { field: 'value', order: 'descending' }, // Sort by value
          axis: { labelLimit: 150 },
        },
        x: {
          field: 'value',
          type: 'quantitative' as const,
          title: xTitle,
        },
        color: { value: color },
        tooltip: [
          { field: 'value', type: 'quantitative' as const, title: 'Count' },
          { field: 'category', type: 'ordinal' as const, title: 'Category' },
        ],
      },
    };
  }

  /**
   * Generate a donut chart specification (pie chart with inner radius)
   */
  static donut(
    data: ChartDataPoint[],
    options: {
      width?: number;
      height?: number;
      colors?: string[];
      innerRadius?: number;
    } = {}
  ): VegaLiteSpec {
    return ChartSpecBuilder.pie(data, {
      ...options,
      innerRadius: options.innerRadius ?? 50,
    });
  }

  /**
   * Generate an area chart specification
   */
  static area(
    data: ChartDataPoint[],
    options: {
      width?: number;
      height?: number;
      xTitle?: string;
      yTitle?: string;
      color?: string;
    } = {}
  ): VegaLiteSpec {
    const {
      width = 600,
      height = 400,
      xTitle = 'Category',
      yTitle = 'Count',
      color = ChartSpecBuilder.DEFAULT_COLOR_PALETTE[2],
    } = options;

    const theme = ChartSpecBuilder.getThemeConfig();

    return {
      $schema: 'https://vega.github.io/schema/vega-lite/v6.json',
      width,
      height,
      background: theme.background,
      padding: { left: 12, top: 12, right: 12, bottom: 12 },
      autosize: {
        type: 'fit',
        contains: 'padding',
      },
      data: { values: data },
      mark: {
        type: 'area' as const,
        opacity: 0.7,
      },
      config: {
        axis: {
          labelColor: theme.textColor,
          titleColor: theme.textColor,
          domainColor: theme.axisColor,
          tickColor: theme.axisColor,
          gridColor: theme.gridColor,
        },
      },
      encoding: {
        x: {
          field: 'category',
          type: 'ordinal' as const,
          title: xTitle,
        },
        y: {
          field: 'value',
          type: 'quantitative' as const,
          title: yTitle,
        },
        color: { value: color },
        tooltip: [
          { field: 'value', type: 'quantitative' as const, title: 'Count' },
          { field: 'category', type: 'ordinal' as const, title: 'Category' },
        ],
      },
    };
  }

  /**
   * Generate a summary/text-only visualization specification
   * This creates a simple visual display showing a large number with optional label
   */
  static summary(
    data: ChartDataPoint[],
    options: {
      width?: number;
      height?: number;
      title?: string;
      color?: string;
    } = {}
  ): VegaLiteSpec {
    const { width = 600, height = 300, title = 'Total Count' } = options;

    const theme = ChartSpecBuilder.getThemeConfig();

    // Calculate total from data
    const total = data.reduce((sum, item) => sum + item.value, 0);

    // Create a simple data structure for the summary display
    const summaryData = [{ label: title, value: total }];

    return {
      $schema: 'https://vega.github.io/schema/vega-lite/v6.json',
      width,
      height,
      background: theme.background,
      padding: { left: 12, top: 12, right: 12, bottom: 12 },
      autosize: {
        type: 'fit',
        contains: 'padding',
      },
      data: { values: summaryData },
      mark: {
        type: 'point' as const,
        opacity: 0, // Make the mark invisible - we only want the text
      },
      encoding: {
        x: { value: width / 2 },
        y: { value: height / 2 },
        text: {
          field: 'value',
          type: 'quantitative' as const,
          format: ',d', // Format with thousands separator
        },
      },
    };
  }
}
